import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { createContext } from 'react';
import type {
  CreateExpenseParams,
  CreateExpenseResponse,
  GetExpensesParams,
  GetExpensesResponse,
  IExpensePhoenixApi,
} from './api';
import { ExpensePhoenixApi } from './api';
import { EXPENSES_PHOENIX_KEY, EXPENSES_PHOENIX_MAIN_KEY } from './queryKeys';
import { PLIANT_TRANSACTIONS_KEY } from '../pliant-phoenix/queryKeys';
import {
  CASH_LIST_TRANSACTIONS_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_PHOENIX_KEY,
} from '../cash-management-phoenix/queryKeys';
import { CASH_TRANSACTION_GROUP_LIST_KEY } from '../cash-management/queryKeys';

export type {
  CreateExpenseBody,
  CreateExpenseParams,
  CreateExpensePart,
  CreateExpenseResponse,
  Expense,
  ExpenseCategory,
  ExpenseCashTransactionGroup,
  ExpenseExportSummary,
  ExpenseListMeta,
  ExpensePart,
  ExpensePartTax,
  ExpenseTab,
  GetExpensesParams,
  GetExpensesResponse,
  IExpensePhoenixApi,
} from './api';

const ApiContext = createContext<IExpensePhoenixApi | undefined>(undefined);
const { useApi: useExpensePhoenixApi, ApiProvider: ExpensePhoenixApiProvider } =
  makeApiProvider({
    name: 'ExpensePhoenix',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new ExpensePhoenixApi(tenantId, auth, apiUrl),
  });

export { ExpensePhoenixApiProvider, useExpensePhoenixApi };

export function useExpenses(
  params: GetExpensesParams['params'],
  options?: Omit<
    UseQueryOptions<GetExpensesResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetExpensesResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    EXPENSES_PHOENIX_KEY(params),
    (api) => {
      return api.getExpenses({ params });
    },
    {
      ...options,
      placeholderData: keepPreviousData,
    }
  );
}

export function useCreateExpense(): UseMutationResult<
  CreateExpenseResponse,
  unknown,
  CreateExpenseParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<CreateExpenseResponse, unknown, CreateExpenseParams>({
    mutationKey: ['expense-create'],
    mutationFn: (data) => api.createExpense(data),
    onSuccess: async (_response, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            variables.body.cash_list_transaction_group_id
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}
